import { useContext, useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom'
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus'
import theme, { AMBER } from './theme'

import { AuthProvider } from './context/AuthContext'
import AuthContext from './context/AuthContext'
import { NotificationProvider } from './context/NotificationContext'
import ErrorBoundary from './components/ErrorBoundary'

import HomePage from './pages/HomePage'
import MapPage from './pages/MapPage'
import LoginPage from './pages/LoginPage'
import Header from './components/Header'

import PrivateRoute from './utils/PrivateRoute'
import CookieBanner from './components/CookieBanner'
import PWAInstallBanner from './components/PWAInstallBanner'
import UpdateBanner from './components/UpdateBanner'
import HeaderMUI from './components/HeaderMUI'
import StudentsPage from './pages/StudentsPage'
import StudentDetailsPage from './pages/StudentDetailsPage'
import LoginMUIPage from './pages/LoginMUIPage'
import Page404 from './pages/404Page'
import MapboxMapPage from './pages/StudentPOIsPage'
import StudentPOIsPage from './pages/StudentPOIsPage'
import RoutesPage from './pages/RoutesPage'
import ParentsPage from './pages/ParentsPage'
import AddParentPage from './pages/AddParentPage'
import DepartmentsPage from './pages/DepartmentsPage'
import FoodPage from './pages/FoodPage'
import DriversPage from './pages/DriversPage'
import DriverDetailsPage from './pages/DriverDetailsPage'
import TeachersPage from './pages/TeachersPage'
import TeacherDetailsPage from './pages/TeacherDetailsPage'
import AttendancePage from './pages/AttendancePage'
import SchoolPage from './pages/SchoolPage'
import MessagesPage from './pages/MessagesPage'
import ReportsPage from './pages/ReportsPage'
import AppErrorsPage from './pages/AppErrorsPage'
import PreferencesPage from './pages/PreferencesPage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import PrivacyDriverPage from './pages/PrivacyDriverPage'
import DriverMapPage from './pages/DriverMapPage'
import DriverSettingsPage from './pages/DriverSettingsPage'
import ParentTodayPage from './pages/ParentTodayPage'
import ParentMessagesPage from './pages/ParentMessagesPage'
import ParentChildrenPage from './pages/ParentChildrenPage'
import ParentSettingsPage from './pages/ParentSettingsPage'
import ParentBusTrackingPage from './pages/ParentBusTrackingPage'
import ParentMenuPage from './pages/ParentMenuPage'
import ParentStatsPage from './pages/ParentStatsPage'
import ParentHelpPage from './pages/ParentHelpPage'

function RoleBasedHome() {
    const { userRole } = useContext(AuthContext);
    if (userRole === 2) return <Navigate to="/driver/map" replace />;
    if (userRole === 1) return <Navigate to="/parent/today" replace />;
    return <AttendancePage />;
}

function RoleBasedPreferences() {
    const { userRole } = useContext(AuthContext);
    if (userRole === 1) return <Navigate to="/parent/settings" replace />;
    if (userRole === 2) return <Navigate to="/driver/settings" replace />;
    return <PreferencesPage />;
}

// Global bus-approaching banner shown on all parent pages except the tracking page itself.
// Also auto-navigates to the tracking page (with token) when the user is already on
// /parent/bus-tracking but has no token — fixing the "page never auto-starts" bug.
function ParentTrackingBanner() {
    const { userRole, authTokens, backendUrl } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);

    useEffect(() => {
        if (userRole !== 1 || !authTokens) { setSessions([]); return; }
        // Use a ref-like pattern via a local variable to avoid stale-closure issues
        // when authTokens reference changes on token refresh.
        const headers = { Authorization: 'Bearer ' + authTokens.access };
        const load = () => {
            fetch(backendUrl + 'api/parent/tracking-session/', { headers })
                .then(r => r.ok ? r.json() : null)
                .then(data => setSessions(data?.status === 'active' ? data.sessions : []))
                .catch(() => {});
        };
        load();
        const id = setInterval(load, 10000);
        return () => clearInterval(id);
    }, [userRole, authTokens, backendUrl]);

    // When the user is on /parent/bus-tracking without a token and a session appears,
    // inject the token into the URL so the tracking page auto-starts.
    useEffect(() => {
        if (location.pathname !== '/parent/bus-tracking') return;
        const currentToken = new URLSearchParams(location.search).get('token');
        if (!currentToken && sessions.length > 0) {
            navigate(`/parent/bus-tracking?token=${sessions[0].token}`, { replace: true });
        }
    }, [location.pathname, location.search, sessions, navigate]);

    if (userRole !== 1 || sessions.length === 0 || location.pathname === '/parent/bus-tracking') return null;

    return (
        <Box>
            {sessions.map(s => (
                <Box
                    key={s.token}
                    onClick={() => navigate(`/parent/bus-tracking?token=${s.token}`)}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 2, py: 1.25, background: AMBER,
                        cursor: 'pointer', '&:hover': { opacity: 0.9 },
                    }}
                >
                    <DirectionsBusIcon sx={{ color: '#fff', fontSize: 20 }} />
                    <Typography sx={{ flex: 1, color: '#fff', fontWeight: 700, fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem' }}>
                        The bus is heading to {s.child_name}'s stop
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem' }}>
                        Click to track →
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// Redirects teachers (role=3) away from admin-only pages
function AdminOnlyRoute({ children }) {
    const { userRole } = useContext(AuthContext);
    if (userRole === 3) return <Navigate to="/" replace />;
    return children;
}

// Combines PrivateRoute auth-guard with a per-route ErrorBoundary so a crash
// in one page doesn't take down the whole app.
function BoundedRoute({ children }) {
    return (
        <PrivateRoute>
            <ErrorBoundary>{children}</ErrorBoundary>
        </PrivateRoute>
    );
}

function App() {
    useEffect(() => {
        if (!('wakeLock' in navigator)) return;
        let lock = null;
        const acquire = () =>
            navigator.wakeLock.request('screen').then(l => { lock = l; }).catch(() => {});
        acquire();
        const onVisibility = () => { if (document.visibilityState === 'visible') acquire(); };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            lock?.release();
        };
    }, []);

    return (
        <ThemeProvider theme={theme}>
        <div className="App">
            <Router>
                <AuthProvider>
                    <NotificationProvider>
                    <HeaderMUI />
                    <UpdateBanner />
                    <CookieBanner />
                    <PWAInstallBanner />
                    <ParentTrackingBanner />
                    <ErrorBoundary>
                    <Box sx={{ pl: 'var(--sal)', pr: 'var(--sar)', pb: 'var(--sab)' }}>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/privacy-driver" element={<PrivacyDriverPage />} />
                        <Route path="/" element={
                            <BoundedRoute>
                                <RoleBasedHome />
                            </BoundedRoute>} />

                        <Route path="/map" element={
                            <BoundedRoute>
                                <AdminOnlyRoute><MapPage /></AdminOnlyRoute>
                            </BoundedRoute>} />

                        <Route path="/students" element={
                            <BoundedRoute>
                                <StudentsPage />
                            </BoundedRoute>} />

                        <Route path="/Routes" element={
                            <BoundedRoute>
                                <AdminOnlyRoute><RoutesPage /></AdminOnlyRoute>
                            </BoundedRoute>} />

                        <Route path="/Parents" element={
                            <BoundedRoute>
                                <ParentsPage />
                            </BoundedRoute>} />

                        <Route path="/Departments" element={
                            <BoundedRoute>
                                <DepartmentsPage />
                            </BoundedRoute>} />

                        <Route path="/student/:student_id" element={
                            <BoundedRoute>
                                <StudentDetailsPage />
                            </BoundedRoute>} />
                        <Route path="/add-student" element={
                            <BoundedRoute>
                                <StudentDetailsPage />
                            </BoundedRoute>} />
                        <Route path="/add-parent" element={
                            <BoundedRoute>
                                <AddParentPage />
                            </BoundedRoute>} />
                        <Route path="/add-parent/:profile_id" element={
                            <BoundedRoute>
                                <AddParentPage />
                            </BoundedRoute>} />
                        <Route path="/parent/:profile_id" element={
                            <BoundedRoute>
                                <AddParentPage />
                            </BoundedRoute>} />
                        <Route path="/student-address/:student_id" element={
                            <BoundedRoute>
                                <StudentPOIsPage />
                            </BoundedRoute>} />

                        <Route path="/Food" element={
                            <BoundedRoute>
                                <FoodPage />
                            </BoundedRoute>} />

                        <Route path="/drivers" element={
                            <BoundedRoute>
                                <AdminOnlyRoute><DriversPage /></AdminOnlyRoute>
                            </BoundedRoute>} />

                        <Route path="/add-driver" element={
                            <BoundedRoute>
                                <AdminOnlyRoute><DriverDetailsPage /></AdminOnlyRoute>
                            </BoundedRoute>} />

                        <Route path="/driver/:profile_id" element={
                            <BoundedRoute>
                                <AdminOnlyRoute><DriverDetailsPage /></AdminOnlyRoute>
                            </BoundedRoute>} />

                        <Route path="/teachers" element={
                            <BoundedRoute>
                                <AdminOnlyRoute><TeachersPage /></AdminOnlyRoute>
                            </BoundedRoute>} />

                        <Route path="/add-teacher" element={
                            <BoundedRoute>
                                <AdminOnlyRoute><TeacherDetailsPage /></AdminOnlyRoute>
                            </BoundedRoute>} />

                        <Route path="/teacher/:profile_id" element={
                            <BoundedRoute>
                                <AdminOnlyRoute><TeacherDetailsPage /></AdminOnlyRoute>
                            </BoundedRoute>} />

                        <Route path="/attendance" element={
                            <BoundedRoute>
                                <AttendancePage />
                            </BoundedRoute>} />

                        <Route path="/school" element={
                            <BoundedRoute>
                                <SchoolPage />
                            </BoundedRoute>} />

                        <Route path="/messages" element={
                            <BoundedRoute>
                                <MessagesPage />
                            </BoundedRoute>} />

                        <Route path="/reports" element={
                            <BoundedRoute>
                                <ReportsPage />
                            </BoundedRoute>} />

                        <Route path="/app-errors" element={
                            <BoundedRoute>
                                <AppErrorsPage />
                            </BoundedRoute>} />

                        <Route path="/preferences" element={
                            <BoundedRoute>
                                <RoleBasedPreferences />
                            </BoundedRoute>} />

                        {/* Driver routes */}
                        <Route path="/driver/map" element={
                            <BoundedRoute>
                                <DriverMapPage />
                            </BoundedRoute>} />
                        <Route path="/driver/settings" element={
                            <BoundedRoute>
                                <DriverSettingsPage />
                            </BoundedRoute>} />

                        {/* Parent routes */}
                        <Route path="/parent/today" element={
                            <BoundedRoute>
                                <ParentTodayPage />
                            </BoundedRoute>} />
                        <Route path="/parent/messages" element={
                            <BoundedRoute>
                                <ParentMessagesPage />
                            </BoundedRoute>} />
                        <Route path="/parent/children" element={
                            <BoundedRoute>
                                <ParentChildrenPage />
                            </BoundedRoute>} />
                        <Route path="/parent/settings" element={
                            <BoundedRoute>
                                <ParentSettingsPage />
                            </BoundedRoute>} />
                        <Route path="/parent/menu" element={
                            <BoundedRoute>
                                <ParentMenuPage />
                            </BoundedRoute>} />
                        <Route path="/parent/stats" element={
                            <BoundedRoute>
                                <ParentStatsPage />
                            </BoundedRoute>} />
                        <Route path="/parent/bus-tracking" element={
                            <BoundedRoute>
                                <ParentBusTrackingPage />
                            </BoundedRoute>} />
                        <Route path="/parent/help" element={
                            <BoundedRoute>
                                <ParentHelpPage />
                            </BoundedRoute>} />

                        <Route path="*" element={<Page404 />} />
                    </Routes>
                    </Box>
                    </ErrorBoundary>
                    </NotificationProvider>
                </AuthProvider>
            </Router>
        </div>
        </ThemeProvider>
    );
}

export default App;